import { defineConfig } from '@chakra-ui/react';

const baseSemanticColors = {
  text: {
    primary: {
      value: { base: '{colors.gray.600}', _dark: '{colors.darkgray.600}' },
    },
    secondary: {
      value: { base: '{colors.gray.500}', _dark: '{colors.darkgray.500}' },
    },
    placeholder: {
      value: { base: '{colors.gray.400}', _dark: '{colors.darkgray.400}' },
    },
    selected: {
      value: { base: '{colors.selection.500}', _dark: '{colors.selection.500}' },
    },
    error: {
      value: { base: '{colors.error.500}', _dark: '{colors.error.500}' },
    },
    inverse: {
      value: { base: '{colors.white}', _dark: '{colors.darkwhite}' },
    },
  },
  surface: {
    background: {
      value: { base: '{colors.white}', _dark: '{colors.darkwhite}' },
    },
    hover: {
      value: { base: '{colors.gray.100}', _dark: '{colors.darkgray.100}' },
    },
    inverse: {
      value: { base: '{colors.gray.800}', _dark: '{colors.darkgray.800}' },
    },
  },
  border: {
    light: {
      value: { base: '{colors.gray.200}', _dark: '{colors.darkgray.200}' },
    },
    dark: {
      value: { base: '{colors.gray.400}', _dark: '{colors.darkgray.400}' },
    },
    inverse: {
      value: { base: '{colors.white}', _dark: '{colors.darkwhite}' },
    },
  },
};

const config = defineConfig({
  theme: {
    tokens: {
      colors: {
        white: {
          value: '#FFFFFF',
        },
        darkwhite: {
          value: '#1C1C1B',
        },
        black: {
          value: '#1C1C1B',
        },
        darkblack: {
          value: '#FFFFFF',
        },
        gray: {
          50: {
            value: '#F8F7F6',
          },
          100: {
            value: '#F5F4F2',
          },
          200: {
            value: '#E9E8E6',
          },
          300: {
            value: '#DDDCDB',
          },
          400: {
            value: '#BBBAB9',
          },
          500: {
            value: '#A1A1A0',
          },
          600: {
            value: '#535352',
          },
          700: {
            value: '#272726',
          },
          800: {
            value: '#171716',
          },
        },
        darkgray: {
          50: {
            value: '#242423',
          },
          100: {
            value: '#2E2E2D',
          },
          200: {
            value: '#363635',
          },
          300: {
            value: '#40403F',
          },
          400: {
            value: '#61605F',
          },
          500: {
            value: '#7D7C7A',
          },
          600: {
            value: '#C9C8C7',
          },
          700: {
            value: '#F8F7F6',
          },
          800: {
            value: '#FFFFFF',
          },
        },
        selection: {
          100: {
            value: '#F5FBFF',
          },
          200: {
            value: '#DDF1FE',
          },
          300: {
            value: '#C2E6FE',
          },
          400: {
            value: '#90D2FE',
          },
          500: {
            value: '#18A0FB',
          },
          600: {
            value: '#097FCE',
          },
        },
        darkselection: {
          100: {
            value: '#182430',
          },
          200: {
            value: '#10273B',
          },
          300: {
            value: '#043454',
          },
          400: {
            value: '#0E5E94',
          },
          500: {
            value: '#18A0FB',
          },
          600: {
            value: '#90D2FE',
          },
        },
        error: {
          100: {
            value: '#FFF6F5',
          },
          200: {
            value: '#FFE4E1',
          },
          300: {
            value: '#FEC5BD',
          },
          400: {
            value: '#FD998B',
          },
          500: {
            value: '#FA5742',
          },
          600: {
            value: '#C8311E',
          },
        },
        darkerror: {
          100: {
            value: '#300C07',
          },
          200: {
            value: '#45120B',
          },
          300: {
            value: '#54150D',
          },
          400: {
            value: '#942517',
          },
          500: {
            value: '#FA5742',
          },
          600: {
            value: '#FD998B',
          },
        },
      },
    },
    semanticTokens: {
      colors: {
        surface: baseSemanticColors.surface,
        text: baseSemanticColors.text,
        border: baseSemanticColors.border,
        sidebar: {
          background: {
            value: { base: '{colors.gray.50}', _dark: '{colors.darkgray.50}' },
          },
          hover: {
            value: { base: '{colors.gray.200}', _dark: '{colors.darkgray.200}' },
          },
          hoverButton: {
            value: { base: '{colors.gray.300}', _dark: '{colors.darkgray.300}' },
          },
        },
        icon: {
          primary: {
            value: { base: '{colors.gray.600}', _dark: '{colors.darkgray.600}' },
          },
          secondary: {
            value: { base: '{colors.gray.500}', _dark: '{colors.darkgray.500}' },
          },
          tertiary: {
            value: { base: '{colors.gray.400}', _dark: '{colors.darkgray.400}' },
          },
          selected: {
            value: { base: '{colors.selection.500}', _dark: '{colors.selection.500}' },
          },
          error: {
            value: { base: '{colors.error.500}', _dark: '{colors.error.500}' },
          },
          inverse: {
            value: { base: '{colors.white}', _dark: '{colors.darkwhite}' },
          },
        },
        tooltip: {
          background: {
            value: baseSemanticColors.surface.inverse,
          },
          color: {
            value: baseSemanticColors.text.inverse,
          },
        },
        input: {
          background: {
            value: { base: '{colors.gray.100}', _dark: '{colors.darkgray.100}' },
          },
          backgroundDisabled: {
            value: { base: '{colors.gray.100}', _dark: '{colors.darkgray.100}' },
          },
          border: {
            value: baseSemanticColors.border.light,
          },
          borderHover: {
            value: { base: '{colors.selection.400}', _dark: '{colors.darkselection.400}' },
          },
          borderFocus: {
            value: { base: '{colors.selection.500}', _dark: '{colors.darkselection.500}' },
          },
          color: {
            value: baseSemanticColors.text.primary,
          },
        },
        button: {
          primary: {
            background: {
              value: { base: '{colors.selection.100}', _dark: '{colors.darkselection.100}' },
            },
            border: {
              value: { base: '{colors.selection.300}', _dark: '{colors.darkselection.300}' },
            },
            color: baseSemanticColors.text.selected,
            hover: {
              value: { base: '{colors.selection.200}', _dark: '{colors.darkselection.200}' },
            },
          },
          secondary: {
            background: baseSemanticColors.surface.background,
            border: {
              value: baseSemanticColors.border.light,
            },
            color: {
              value: baseSemanticColors.text.primary,
            },
            hover: {
              value: { base: '{colors.gray.100}', _dark: '{colors.darkgray.100}' },
            },
          },
          error: {
            background: {
              value: { base: '{colors.error.100}', _dark: '{colors.darkerror.100}' },
            },
            border: {
              value: { base: '{colors.error.300}', _dark: '{colors.darkerror.300}' },
            },
            color: {
              value: baseSemanticColors.text.error,
            },
            hover: {
              value: { base: '{colors.error.200}', _dark: '{colors.darkerror.200}' },
            },
          },
        },
      },
    },
  },
});

export default config;
